import { Trans } from '@lingui/macro';
import { t } from '@lingui/macro';
import { FC, Suspense, memo, useEffect } from 'react';

import { AppButton, TextInput } from '@/UI';
import AuthLayout from '@/UI/auth-layout';
import LinearLoader from '@/UI/loaders/LinearLoader';
import upload from '@/assets/icons/document-upload.svg';
import useCheckAdmin from '@/hooks/use-check-admin';
import { useAppDispatch } from '@/hooks/useRedux';
import { p2pActions } from '@/store/p2p-slice';

import CustomErrorBoundary from '../CustomErrorBoundary/CustomErrorBoundary';

import P2PLogin from './P2PLogin';
import classes from './UploadServerKeys.module.css';
import useP2PKeyExchange from './useP2PKeyExchanged';
import useUploadServerKeys from './useUploadServerKeys';

const AdminInterface: FC = () => {
    const { files, handleChange, handleAddPrivateKeys, serverData } =
        useUploadServerKeys();

    return (
        <AuthLayout
            header={<Trans>Upload Encrypted Keys</Trans>}
            subHeader={serverData?.name && serverData.name}>
            <div className={classes['inputWrapper']}>
                <TextInput
                    label={t`Private Key`}
                    name="privateKey"
                    isDisabled
                    icon={upload}
                    value={serverData?.privateKey ?? files?.fileName ?? ''}
                    specialCase="file-picker"
                    placeHolder={t`Upload your private key`}
                    onClick={handleChange}
                    error={files?.error}
                />
            </div>
            <P2PLogin />

            <AppButton
                title={t`Continue`}
                onClick={handleAddPrivateKeys}
                classStyles={classes['appButton']}
            />
        </AuthLayout>
    );
};

const UserInterface: FC = () => {
    return (
        <AuthLayout header={<Trans>receive server keys</Trans>}>
            <P2PLogin />
        </AuthLayout>
    );
};

const LoadingContent: FC = () => {
    const dispatch = useAppDispatch();
    const { isLoading, isAdmin } = useCheckAdmin();

    useP2PKeyExchange();

    useEffect(() => {
        dispatch(p2pActions.updateIsInKeyUpload(true));
    }, [dispatch]);

    if (isLoading) return <LinearLoader />;

    return isAdmin || true ? <AdminInterface /> : <UserInterface />;
};

const UploadServerKeys: FC = () => {
    return (
        <CustomErrorBoundary showErrorDetail>
            <Suspense fallback={'loading permissions'}>
                <LoadingContent />
            </Suspense>
        </CustomErrorBoundary>
    );
};

export default memo(UploadServerKeys);
